import { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Users, Package, Mountain, MapPin, Leaf, ShieldCheck, UserCog,
  FileText, Settings as SettingsIcon, Menu, Share2, X, LogOut, Mail, BookUser, MailPlus,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Copy, Check as CheckIcon } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/hub/lib/api";
import { useAuth } from "@/hub/auth/AuthContext";

const BASE_NAV = [
  { to: "/hub", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/hub/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/hub/groups", label: "Clients", icon: Users, testid: "nav-groups" },
  { to: "/hub/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" },
  { to: "/hub/mailbox", label: "Mailbox", icon: Mail, testid: "nav-mailbox" },
  { to: "/hub/contacts", label: "Contacts", icon: BookUser, testid: "nav-contacts" },
  { to: "/hub/email", label: "Email Center", icon: MailPlus, testid: "nav-email" },
  { to: "/hub/packages", label: "Packages", icon: Package, testid: "nav-packages" },
  { to: "/hub/activities", label: "Activities", icon: Mountain, testid: "nav-activities" },
  { to: "/hub/zones", label: "Zones", icon: MapPin, testid: "nav-zones" },
];

export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role === "admin" ? "Admin" : "RM";
  const [rms, setRms] = useState([]);
  const [rmCode, setRmCode] = useState(() => localStorage.getItem("lush_rm_code") || "");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const location = useLocation();
  const bookingUrl = `${window.location.origin}/book`;

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(bookingUrl);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = bookingUrl; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select();
      try { document.execCommand("copy"); } catch (_) {}
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Booking link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "LUSH Booking", text: "Book your escape at LUSH", url: bookingUrl }); } catch (e) { /* cancelled */ }
    } else { copyLink(); }
  };

  useEffect(() => {
    localStorage.setItem("lush_role", role);
    window.dispatchEvent(new Event("role-change"));
  }, [role]);

  const loadRms = () => api.users("RM").then((u) => setRms(u.filter((x) => x.active)));
  useEffect(() => {
    loadRms();
    window.addEventListener("users-change", loadRms);
    return () => window.removeEventListener("users-change", loadRms);
  }, []);

  const pickRm = (code) => {
    const u = rms.find((r) => r.code === code);
    setRmCode(code);
    localStorage.setItem("lush_rm_code", code);
    localStorage.setItem("lush_rm_name", u ? u.name : "");
    window.dispatchEvent(new Event("rm-change"));
  };

  const nav = role === "Admin"
    ? [...BASE_NAV, { to: "/hub/settings", label: "Admin Settings", icon: SettingsIcon, testid: "nav-settings" }]
    : BASE_NAV;

  const title = nav.find((n) => n.to === location.pathname || (n.to !== "/hub" && location.pathname.startsWith(n.to)))?.label || "Dashboard";

  return (
    <div className="flex min-h-screen bg-background">
      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <aside className={`w-60 shrink-0 border-r border-border bg-white flex flex-col fixed h-screen z-40 transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="h-16 flex items-center gap-2 px-5 border-b border-border">
          <div className="h-9 w-9 bg-forest flex items-center justify-center rounded-sm">
            <Leaf className="h-5 w-5 text-white" strokeWidth={1.8} />
          </div>
          <div>
            <div className="font-heading font-bold text-forest leading-tight tracking-tight">LUSH</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-stone-400">Operations</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto lush-scroll">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              end={item.to === "/hub"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors duration-150 ${
                  isActive ? "bg-forest text-white" : "text-stone-600 hover:bg-stone-100 hover:text-forest"
                }`
              }
            >
              <item.icon className="h-4 w-4" strokeWidth={1.6} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border space-y-3">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 shrink-0 rounded-full bg-forest text-white flex items-center justify-center text-sm font-semibold">
              {(user?.name || "U").charAt(0)}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-stone-800 truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-stone-400">{role === "Admin" ? "Administrator" : "Relationship Manager"}</div>
            </div>
          </div>
          {role === "RM" && (
            <div>
              <span className="text-[10px] uppercase tracking-[0.15em] text-stone-400 block mb-1">Acting RM</span>
              {rms.length === 0 ? (
                <p className="text-[11px] text-stone-400">No RM codes yet. Ask an admin to create one in Settings.</p>
              ) : (
                <Select value={rmCode} onValueChange={pickRm}>
                  <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="rm-select"><SelectValue placeholder="Select your code" /></SelectTrigger>
                  <SelectContent>
                    {rms.map((r) => <SelectItem key={r.id} value={r.code}>{r.name} · {r.code}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
          <button
            onClick={() => { logout(); navigate("/login"); }}
            data-testid="logout-button"
            className="flex items-center gap-2 text-xs text-stone-500 hover:text-terracotta transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-60 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-white/80 backdrop-blur-md flex items-center justify-between px-4 lg:px-8 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5 text-stone-600" /></button>
            <h1 className="font-heading text-lg lg:text-xl font-semibold tracking-tight text-stone-900" data-testid="page-title">{title}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setShareOpen(true)} data-testid="share-booking-link"><Share2 className="h-4 w-4 lg:mr-1.5" /><span className="hidden lg:inline">Share Booking Link</span></Button>
            <span className="font-mono text-xs text-stone-400 hidden sm:inline">{new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
          </div>
        </header>
        <main className="flex-1 p-8 lush-scroll">
          <Outlet />
        </main>
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="share-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">Client Booking Link</DialogTitle>
            <DialogDescription>Share this link so clients can book their own reservation on any device.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-stretch gap-2">
              <input readOnly value={bookingUrl} data-testid="share-link-url"
                className="flex-1 min-w-0 border border-stone-200 rounded-sm px-3 py-2 text-sm bg-stone-50 font-mono text-stone-600 truncate" />
              <Button onClick={copyLink} className="rounded-sm bg-forest hover:bg-[#142F27] shrink-0" data-testid="share-link-copy">
                {copied ? <CheckIcon className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                <span className="ml-1.5">{copied ? "Copied" : "Copy"}</span>
              </Button>
            </div>
            {typeof navigator !== "undefined" && navigator.share && (
              <Button variant="outline" onClick={nativeShare} className="w-full rounded-sm" data-testid="share-link-native">
                <Share2 className="h-4 w-4 mr-1.5" /> Share via…
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
